import React, { FunctionComponent } from 'react';
import { FileVideoIcon, VideoIcon } from 'lucide-react';
import * as Sentry from '@sentry/browser';
import { NativeCapture, Text } from '@companion-professional/components';
import { isMobile } from '../../lib/ua';
import { TapButton } from '../TapButton';
import { trackEvent } from '../../lib/trackEvent';
import { useNavigate } from 'react-router-dom';

interface NativeCameraCaptureButtonsProps {
  // onVideoSelected is a callback function that is called when a video has been filmed using the native camera or
  // selected from the file picker
  onVideoSelected: (file: File) => void;
}

// NativeCameraCaptureButtons is a component that displays a button that allows the user to capture a video using the
// native camera app (if mobile) and a button for selecting a video from the file picker.  When a video is accepted
// ("use this video") or selected with the file picker, the onVideoSelected callback is called with the selected file.
// TODO: SEPARATE OUT THE CAPTURE AND UPLOAD LOGIC FROM BUTTON COMPONENT !
export const NativeCameraCaptureButtons: FunctionComponent<NativeCameraCaptureButtonsProps> = ({ }) => {
  let moreOptionsText = 'Upload Video';
  if (isMobile()) {
    moreOptionsText = 'Upload File Options';
  }

  const navigate = useNavigate();

  const uploadOrCaptureNavigation = (file: File) => {
    // Navigate to the video page which handles upload status and error handling.
    // When a video file is selected, it's stored in state and passed to the next page
    // which manages the Mux upload process.
    navigate('../video', { state: { file } });
  }

  return (
    <div className="flex w-full h-full items-center justify-center">
      <div className="flex flex-col items-center justify-center space-y-1 sm:space-y-2 w-full max-w-sm sm:max-w-md md:max-w-lg">
        {isMobile() ? (
          <NativeCapture captureType="video" onFileSelected={uploadOrCaptureNavigation} className="w-full">
            <TapButton
              onClick={() => {
                trackEvent('video_selection_initiated', {
                  method: 'native_camera'
                });
                Sentry.addBreadcrumb({
                  category: 'checkup_flow',
                  message: 'Native camera capture opened'
                });
              }}
            >
              <VideoIcon className="hidden sm:block" /> Capture Video
            </TapButton>
          </NativeCapture>
        ) : null}

        {isMobile() ? (
          <Text as="div" size="sm" weight="strong" className="sm:text-base">
            - or -
          </Text>
        ) : null}

        <NativeCapture captureType="video" nativeFilePicker onFileSelected={uploadOrCaptureNavigation} className="w-full">
          <TapButton
            onClick={() => {
              trackEvent('video_selection_initiated', {
                method: 'file_picker'
              });
              Sentry.addBreadcrumb({
                category: 'checkup_flow',
                message: 'File picker opened'
              });
            }}
          >
            <FileVideoIcon className="hidden sm:block" /> {moreOptionsText}
          </TapButton>
        </NativeCapture>
      </div>
    </div>
  );
};
